import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { products, Product } from '../data/products';
import ContactUsButton from '../components/ContactUsButton';
import SEO from '../components/SEO';

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t, i18n } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const { openModal } = useCart();
  const currentLang = i18n.language as 'en' | 'he';

  const product = products.find((p: Product) => p.id === Number(id));

  // Keyboard navigation for images
  useEffect(() => {
    if (!product?.images || product.images.length <= 1) return;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          setSelectedImage((prev) => 
            prev === 0 ? product.images!.length - 1 : prev - 1
          );
          break;
        case 'ArrowRight':
          event.preventDefault();
          setSelectedImage((prev) => 
            prev === product.images!.length - 1 ? 0 : prev + 1
          );
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [product?.images]);

  if (!product) {
    navigate('/products');
    return null;
  }

  // SEO structured data for product
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.translations[currentLang].name,
    "description": product.translations[currentLang].description || product.translations[currentLang].name,
    "image": product.images && product.images.length > 0 ? product.images[0] : product.image,
    "brand": {
      "@type": "Brand",
      "name": "BlueBoard"
    },
    "offers": {
      "@type": "Offer",
      "price": typeof product.price === 'number' ? product.price : 0,
      "priceCurrency": "ILS",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "BlueBoard"
      }
    },
    "category": "Electric Skateboards"
  };

  // Related products (exclude current)
  const related = products.filter((p) => p.id !== product.id).slice(0, 4);

  // Product Details Section - moved above related products
  const detailsText = product.translations?.[currentLang]?.details || product.details;

  return (
    <>
      <SEO
        title={`${product.translations[currentLang].name} - BlueBoard Electric Skateboards`}
        description={product.translations[currentLang].description || `Discover ${product.translations[currentLang].name} - Premium electric skateboard from BlueBoard. Expert support in Israel.`}
        keywords={`${product.translations[currentLang].name}, electric skateboard, BlueBoard, Israel, ${product.translations[currentLang].name.toLowerCase()}`}
        image={product.images && product.images.length > 0 ? product.images[0] : product.image}
        url={`/products/${product.id}`}
        type="product"
        lang={currentLang}
        structuredData={structuredData}
      />
      <div className="bg-white min-h-screen py-12" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="md:w-1/2 w-full">
            <div className="relative">
              <img
                src={product.images?.[selectedImage] || product.image}
                alt={product.translations[currentLang].name}
                className="w-full h-96 object-contain bg-gray-100 rounded-lg shadow-md cursor-zoom-in transition-opacity duration-300"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                onClick={() => setZoomOpen(true)}
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.opacity = '0';
                  img.style.transition = 'opacity 0.3s ease-in';
                  setTimeout(() => {
                    img.style.opacity = '1';
                  }, 50);
                }}
                style={{ opacity: 0 }}
              />
              
              {/* Navigation buttons for multiple images */}
              {product.images && product.images.length > 1 && (
                <>
                  {/* Previous button */}
                  <button
                    onClick={() => setSelectedImage((prev) => 
                      prev === 0 ? product.images!.length - 1 : prev - 1
                    )}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full p-3 transition-all duration-200 z-10 shadow-lg hover:scale-110"
                    aria-label="Previous image"
                    title="Previous image (←)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                  
                  {/* Next button */}
                  <button
                    onClick={() => setSelectedImage((prev) => 
                      prev === product.images!.length - 1 ? 0 : prev + 1
                    )}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full p-3 transition-all duration-200 z-10 shadow-lg hover:scale-110"
                    aria-label="Next image"
                    title="Next image (→)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                  
                  {/* Image counter */}
                  <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {selectedImage + 1} / {product.images.length}
                  </div>
                </>
              )}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 mt-4 justify-center">
                  {product.images.map((img, idx) => (
                    <img
                      key={img}
                      src={img}
                      alt={product.translations[currentLang].name + ' ' + (idx + 1)}
                      className={`w-16 h-16 object-cover rounded border cursor-pointer ${selectedImage === idx ? 'border-blue-500' : 'border-gray-300'}`}
                      loading="lazy"
                      decoding="async"
                      onClick={() => setSelectedImage(idx)}
                    />
                  ))}
                </div>
              )}
              {zoomOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={() => setZoomOpen(false)}>
                  <img
                    src={product.images?.[selectedImage] || product.image}
                    alt={product.translations[currentLang].name}
                    className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl border-4 border-white"
                  />
                  
                  {/* Navigation buttons in zoom modal */}
                  {product.images && product.images.length > 1 && (
                    <>
                      {/* Previous button in zoom */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage((prev) => 
                            prev === 0 ? product.images!.length - 1 : prev - 1
                          );
                        }}
                        className="absolute left-8 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full p-4 transition-all duration-200 z-10 shadow-lg hover:scale-110"
                        aria-label="Previous image"
                        title="Previous image (←)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-8 h-8">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>
                      </button>
                      
                      {/* Next button in zoom */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage((prev) => 
                            prev === product.images!.length - 1 ? 0 : prev + 1
                          );
                        }}
                        className="absolute right-8 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full p-4 transition-all duration-200 z-10 shadow-lg hover:scale-110"
                        aria-label="Next image"
                        title="Next image (→)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-8 h-8">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </button>
                      
                      {/* Image counter in zoom */}
                      <div className="absolute bottom-8 right-8 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-lg font-medium">
                        {selectedImage + 1} / {product.images.length}
                      </div>
                    </>
                  )}
                  
                  <button
                    className="absolute top-4 right-4 text-white text-3xl font-bold bg-black bg-opacity-50 rounded-full px-3 py-1"
                    onClick={e => { e.stopPropagation(); setZoomOpen(false); }}
                  >&times;</button>
                </div>
              )}
            </div>
          </div>
          <div className="md:w-1/2 w-full flex flex-col gap-4">
            <h1 className="text-3xl font-bold mb-2">{product.translations[currentLang].name}</h1>
            {/* Product Details Section - moved directly under name */}
            {detailsText && (
              <div className={`mb-4${i18n.language === 'he' ? ' text-right' : ''}`} dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
                <h2 className="text-xl font-bold mb-2">{t('products.details', i18n.language === 'he' ? 'פרטי המוצר' : 'Details')}</h2>
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">{detailsText}</p>
              </div>
            )}
            {/* Payment/Contact Button Section - after details */}
            {typeof product.price === 'number' ? (
              <>
                <div className="text-2xl font-semibold text-blue-700 mb-2">₪ {product.price.toLocaleString()}</div>
                <div className="flex items-center gap-2 mb-4">
                  <span>{t('cart.quantity')} *</span>
                  <button
                    className="px-2 py-1 bg-gray-200 rounded"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  >-
                  </button>
                  <span className="px-3">{quantity}</span>
                  <button
                    className="px-2 py-1 bg-gray-200 rounded"
                    onClick={() => setQuantity((q) => q + 1)}
                  >+
                  </button>
                </div>
                <button
                  className="w-full bg-blue-500 text-white py-3 rounded-lg font-bold hover:bg-blue-600 transition"
                  onClick={() => {
                    if (typeof product.price === 'number') {
                      addToCart({
                        id: product.id,
                        name: product.translations[currentLang].name,
                        price: product.price,
                        image: product.images?.[selectedImage] || product.image
                      }, quantity);
                    }
                  }}
                >
                  {t('cart.addToCart')}
                </button>
                <button
                  className="w-full bg-black text-white py-3 rounded-lg font-bold hover:bg-gray-800 transition"
                  onClick={openModal}
                >
                  {t('cart.quickBuy')}
                </button>
              </>
            ) : (
              <ContactUsButton className="w-full mt-4" />
            )}
          </div>
        </div>
        {product.specs && product.specs.length > 0 && (
          <div className="mt-8 max-w-2xl mx-auto overflow-x-auto">
            <h2 className="text-xl font-bold mb-4">{t('products.specs', 'Technical Specifications')}</h2>
            <table className="min-w-full border border-gray-200 rounded-lg bg-white">
              <tbody>
                {product.specs.map((row, idx) => (
                  <tr key={row.label + idx} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="py-2 px-4 font-medium text-gray-700 whitespace-nowrap border-b border-gray-200">{row.label}</td>
                    <td className="py-2 px-4 text-gray-600 border-b border-gray-200">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Related Products */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">{t('products.relatedProducts')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {related.map((item) => (
              <div 
                key={item.id} 
                className="flex flex-col items-center cursor-pointer hover:shadow-lg transition-shadow duration-300 p-4 rounded-lg"
                onClick={() => navigate(`/products/${item.id}`)}
              >
                <img 
                  src={item.image} 
                  alt={item.translations[currentLang].name} 
                  className="h-32 w-32 object-contain mb-2 bg-gray-100 rounded-lg" 
                />
                <div className="font-semibold text-center">{item.translations[currentLang].name}</div>
                {typeof item.price === 'number' && (
                  <div className="text-gray-700">₪ {item.price.toLocaleString()}</div>
                )}
                {typeof item.price === 'number' && (
                  <button
                    className="w-full bg-black text-white py-2 rounded-lg mt-2 hover:bg-gray-800 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent navigation when clicking the button
                      if (typeof item.price === 'number') {
                        addToCart({
                          id: item.id,
                          name: item.translations[currentLang].name,
                          price: item.price,
                          image: item.images?.[0] || item.image
                        }, 1);
                      }
                    }}
                  >
                    {t('cart.addToCart')}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Modal and Cart Sidebar will be added next */}
      </div>
    </div>
    </>
  );
};

export default ProductDetailsPage; 
